import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { getOrder, getConfig, formatEUR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { CheckCircle2, Mail, Clock, Headphones, Home as HomeIcon } from "lucide-react";
import { SUCCESS } from "@/constants/testIds";
import { trackPurchase } from "@/lib/gtag";

const steps = [
  { icon: Clock, title: "Verifica del pagamento", desc: "Controlliamo la ricezione del pagamento PayPal (di solito entro poche ore)." },
  { icon: Mail, title: "Invio credenziali", desc: "Ti inviamo via email l'accesso al pannello cloud e le istruzioni di installazione." },
  { icon: Headphones, title: "Assistenza dedicata", desc: "Il nostro team ti supporta nella configurazione, passo dopo passo." },
];

export default function Success() {
  const [params] = useSearchParams();
  const orderId = params.get("order");
  const [order, setOrder] = useState(null);
  const [config, setConfig] = useState({ company_email: "" });
  const purchaseFired = useRef(false);

  useEffect(() => {
    if (orderId) getOrder(orderId).then(setOrder).catch(() => {});
    getConfig().then(setConfig).catch(() => {});
  }, [orderId]);

  // Conversione "acquisto" (GA4 purchase + Google Ads), una sola volta per ordine.
  useEffect(() => {
    if (order && !purchaseFired.current) {
      purchaseFired.current = true;
      trackPurchase({
        value: order.total,
        transactionId: order.id,
        items: (order.items || []).map((it) => ({ item_name: it.name, price: it.price, quantity: it.quantity || 1 })),
      });
    }
  }, [order]);

  return (
    <div data-testid={SUCCESS.page} className="container-x py-16 sm:py-24">
      <div className="mx-auto max-w-2xl text-center">
        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-600 text-white glow-strong">
          <CheckCircle2 className="h-8 w-8" />
        </span>
        <h1 className="mt-6 text-3xl sm:text-4xl font-semibold tracking-tight">Grazie per il tuo ordine!</h1>
        <p className="mt-3 text-muted-foreground text-base leading-relaxed">
          Abbiamo registrato la tua richiesta{order ? <> (rif. <strong className="text-foreground">#{order.id.slice(0, 8).toUpperCase()}</strong>)</> : ""}.
          Riceverai a breve l'accesso al pannello cloud via email.
        </p>

        {order && (
          <Card className="mt-8 p-5 rounded-2xl border-border bg-card glow text-left">
            <p className="text-sm font-medium">Dettaglio ordine</p>
            <div className="mt-3 space-y-2">
              {order.items.map((it, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{it.name}</span>
                  <span className="font-medium">{formatEUR(it.price)}</span>
                </div>
              ))}
              <div className="flex items-center justify-between border-t border-border pt-2 mt-2">
                <span className="font-semibold">Totale</span>
                <span className="font-display text-lg font-semibold">{formatEUR(order.total)}</span>
              </div>
            </div>
          </Card>
        )}

        <div className="mt-8 grid sm:grid-cols-3 gap-4 text-left">
          {steps.map((s, i) => (
            <Card key={i} className="p-5 rounded-2xl border-border bg-card glow">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-accent"><s.icon className="h-5 w-5" /></span>
              <h3 className="mt-3 text-sm font-semibold">{s.title}</h3>
              <p className="mt-1 text-xs text-muted-foreground leading-relaxed">{s.desc}</p>
            </Card>
          ))}
        </div>

        {config.company_email && (
          <p className="mt-8 text-sm text-muted-foreground">
            Domande? Scrivici a <a href={`mailto:${config.company_email}`} className="text-accent underline">{config.company_email}</a>
          </p>
        )}

        <Button asChild className="mt-6 rounded-xl glow-hover" data-testid={SUCCESS.home}>
          <Link to="/"><HomeIcon className="h-4 w-4" /> Torna alla home</Link>
        </Button>
      </div>
    </div>
  );
}
